import { useEffect, useMemo, useState } from 'react'
import { Group, TextInput } from '@common/ui'
import { com, Nullable } from '@eidu/web'
import { draftFieldValueToFieldValue, fieldValueToDraftFieldValue } from '../../domain/entity/DraftFieldValue'
import useEntityTypes from '../../io/useEntityTypes'
import useEntityType from '../../io/useEntityType'
import FieldSingleSelect from './FieldSingleSelect'
import { fieldsOfEntityType, findFieldByIdOrFirst } from '../../util/EntityTypeExtensions'
import EntityFormInput from './EntityFormInput'
import EntityTypeId = com.eidu.entity.type.EntityTypeId
import EntityType = com.eidu.entity.type.EntityType
import FieldValue = com.eidu.entity.field.FieldValue
import Field = com.eidu.entity.field.Field
import EntitySelector = com.eidu.entity.selector.EntitySelector
import FieldId = com.eidu.entity.field.FieldId

type FieldValueEqualsInputProps = {
  entityTypeId: EntityTypeId
  selector: EntitySelector.FieldValueEquals | undefined
  setSelector: (selector: EntitySelector.FieldValueEquals) => void
  disabled: boolean
}

type ValueControlProps = {
  field: Field
  value: Nullable<FieldValue>
  setValue: (value: Nullable<FieldValue>) => void
  entityTypes: ReadonlyMap<EntityTypeId, EntityType | null>
  disabled: boolean
}

const ValueControl = ({ field, value, setValue, entityTypes, disabled }: ValueControlProps) => (
  <div style={{ minWidth: 160 }}>
    {field && (
      <EntityFormInput
        key={`selector-value-${field.id.toString()}`}
        field={field}
        fieldValue={field && value ? fieldValueToDraftFieldValue(field.id, value) : undefined}
        entityTypes={entityTypes}
        updateField={(draftFieldValue) => setValue(draftFieldValueToFieldValue(draftFieldValue))}
        disabled={disabled}
      />
    )}
  </div>
)

const FieldValueEqualsInput = ({ entityTypeId, selector, setSelector, disabled }: FieldValueEqualsInputProps) => {
  const { entityTypes } = useEntityTypes()
  const { entityType } = useEntityType(entityTypeId)

  const [localFieldId, setLocalFieldId] = useState<FieldId | undefined>(selector?.fieldId)
  const field = useMemo(() => findFieldByIdOrFirst(entityType ?? undefined, localFieldId), [entityType, localFieldId])
  useEffect(() => {
    if (field && selector) {
      setSelector(selector)
    }
  }, [localFieldId, selector])

  return (
    entityType &&
    entityTypes && (
      <Group gap="xs" align="flex-start" wrap="nowrap">
        <FieldSingleSelect
          label="Field"
          options={fieldsOfEntityType(entityType)}
          entityType={entityType}
          selected={field}
          setSelected={(newField) => setLocalFieldId(newField.id)}
          disabled={disabled}
        />
        {field ? (
          <ValueControl
            entityTypes={entityTypes}
            field={field}
            value={selector?.value}
            setValue={(newValue) => newValue && setSelector(new EntitySelector.FieldValueEquals(field.id, newValue))}
            disabled={disabled}
          />
        ) : (
          <TextInput disabled />
        )}
      </Group>
    )
  )
}

export default FieldValueEqualsInput
